import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Eye, Radio, History, Plus, Search } from "lucide-react";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LifecyclePill } from "@/components/conviction/shared";
import { ConvictionEditor } from "@/components/conviction/ConvictionEditor";
import { HistoryDialog } from "@/components/conviction/HistoryDialog";
import { inp, err, TrackBadge } from "@/components/conviction/studioShared";

export function FeatureToggle() {
  const [on, setOn] = useState(null);
  useEffect(() => { client.get("/features").then((r) => setOn(!!r.data.conviction_v2)); }, []);
  const flip = async () => {
    const reason = window.prompt(on ? "Reason for turning the new Conviction experience OFF?" : "Reason for turning the new Conviction experience ON?");
    if (!reason || reason.length < 3) return;
    try { await client.post("/admin/features/conviction_v2", { enabled: !on, reason }); toast.success(`New Conviction experience ${!on ? "ON" : "OFF"}`); setTimeout(() => window.location.reload(), 600); } catch (e) { err(e); }
  };
  if (on === null) return null;
  return (
    <div className="rounded-xl border border-white/10 p-4 flex flex-wrap items-center justify-between gap-3 mb-6" data-testid="feature-toggle">
      <p className="text-sm">New Conviction experience (V2 + automatic monitor): <b data-testid="feature-state" className={on ? "text-[#ccff00]" : "text-zinc-400"}>{on ? "ON" : "OFF"}</b>
        <span className="block text-xs text-zinc-500">When it's off, members see the legacy conviction pages and the monitor stays idle. Every change is audit-logged.</span></p>
      <button data-testid="feature-toggle-btn" onClick={flip} className={`px-5 py-2 rounded-full text-xs font-semibold ${on ? "border border-white/15" : "bg-[#ccff00] text-black"}`}>{on ? "Turn off" : "Turn on"}</button>
    </div>
  );
}

function LegacyReview({ analysts, reload }) {
  const [legacy, setLegacy] = useState([]);
  const [asg, setAsg] = useState({});
  const loadLegacy = () => client.get("/v2/admin/legacy-review").then((r) => setLegacy(r.data));
  useEffect(() => { loadLegacy(); }, []);
  const assign = async (id) => {
    const v = asg[id] || {};
    if (!v.analyst_id || (v.reason || "").length < 5) { toast.error("Choose the analyst and give a verification reason (5+ characters)."); return; }
    try { await client.post(`/v2/admin/convictions/${id}/assign`, v); toast.success("Ownership recorded"); loadLegacy(); reload(); } catch (e) { err(e); }
  };
  if (!legacy.length) return null;
  return (
    <div className="rounded-xl border border-white/10 p-4 mt-10" data-testid="legacy-review">
      <p className="font-semibold text-sm mb-1">Legacy convictions awaiting owner review ({legacy.length})</p>
      <p className="text-xs text-zinc-500 mb-3">Authors were never guessed. Assign only when the analyst relationship is verified.</p>
      {legacy.map((l) => (
        <div key={l.id} className="border-t border-white/5 py-2 text-sm">
          <p>{l.title} <span className="text-xs text-zinc-500">({l.asset})</span></p>
          <div className="grid sm:grid-cols-3 gap-2 mt-1">
            <select data-testid={`lr-analyst-${l.id}`} className={inp} onChange={(e) => setAsg({ ...asg, [l.id]: { ...asg[l.id], analyst_id: e.target.value } })}><option value="">Analyst</option>{analysts.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select>
            <input data-testid={`lr-reason-${l.id}`} className={inp} placeholder="Verification reason" onChange={(e) => setAsg({ ...asg, [l.id]: { ...asg[l.id], reason: e.target.value } })} />
            <button data-testid={`lr-assign-${l.id}`} onClick={() => assign(l.id)} className="rounded-xl border border-[#ccff00]/40 text-xs text-[#ccff00]">Assign owner</button>
          </div>
        </div>
      ))}
    </div>
  );
}

const Act = ({ testid, onClick, icon: I, children, href }) => {
  const cls = "inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/10 text-xs text-zinc-200 hover:border-[#ccff00]/50 hover:text-[#ccff00]";
  const stop = (e) => e.stopPropagation();
  return href ? <a data-testid={testid} href={href} target="_blank" rel="noreferrer" onClick={stop} className={cls}><I className="w-3.5 h-3.5" />{children}</a>
    : <button data-testid={testid} onClick={(e) => { stop(e); onClick(); }} className={cls}><I className="w-3.5 h-3.5" />{children}</button>;
};

function Row({ c, open, history }) {
  const a = c.asset_info;
  return (
    <div data-testid={`studio-row-${c.id}`} role="button" tabIndex={0} onClick={() => open(c.id)} onKeyDown={(e) => e.key === "Enter" && open(c.id)}
      className="rounded-xl border border-white/10 p-4 cursor-pointer hover:border-white/25 hover:bg-white/[0.02] transition-colors">
      <div className="flex flex-wrap items-start gap-3">
        {c.cover_image ? <img src={c.cover_image} alt="" className="w-16 h-10 rounded-md object-cover" /> : <div className="w-16 h-10 rounded-md bg-white/[0.04]" />}
        <div className="flex-1 min-w-[220px]">
          <div className="flex flex-wrap items-center gap-2"><LifecyclePill value={c.lifecycle} /><TrackBadge t={c.tracking} testid={`studio-tracking-${c.id}`} />{c.published === false && c.lifecycle !== "draft" && <span className="text-[10px] font-mono-cl uppercase text-zinc-500">hidden</span>}{c.featured && <span className="text-[10px] font-mono-cl uppercase text-[#ccff00]">featured</span>}</div>
          <p className="font-semibold mt-1">{c.title}</p>
          <p className="text-xs text-zinc-500">{a ? `${a.ticker} · ${a.asset_class === "stock" ? "Stock" : a.venue || "CEX"} · ${a.exchange}` : c.asset || "No instrument"} · {c.analyst?.name || (c.ownership_status === "unresolved" ? "Analyst not yet verified" : "unassigned")}{c.entry_ref_price ? ` · entry ${c.entry_ref_price}` : ""}{c.levels?.tp1 ? ` · TP ${[c.levels.tp1, c.levels.tp2, c.levels.tp3].filter(Boolean).join("/")} · SL ${c.levels.stop}` : ""}{Number(c.closed_pct) ? ` · closed ${c.closed_pct}%` : ""}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Act testid={`studio-edit-${c.id}`} icon={Pencil} onClick={() => open(c.id)}>Edit</Act>
          <Act testid={`studio-preview-${c.id}`} icon={Eye} href={`/conviction/${c.slug}`}>Preview</Act>
          {c.lifecycle === "active" && <Act testid={`studio-signal-${c.id}`} icon={Radio} onClick={() => open(c.id, "d")}>Add signal</Act>}
          <Act testid={`studio-history-${c.id}`} icon={History} onClick={() => history(c.id)}>History</Act>
        </div>
      </div>
    </div>
  );
}

export function ConvictionStudio({ analysts = [] }) {
  const { isAdmin } = useAuth();
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(null);
  const [hist, setHist] = useState(null);
  const [q, setQ] = useState("");
  const [lc, setLc] = useState("");
  const load = () => client.get("/v2/studio/convictions").then((r) => setD(r.data)).catch(err);
  useEffect(() => { load(); }, []);
  if (edit) return <ConvictionEditor id={edit.id} focus={edit.focus} analysts={analysts} isAdmin={isAdmin} onClose={() => { setEdit(null); load(); }} />;
  if (!d) return null;
  const rows = d.convictions.filter((c) => (!lc || c.lifecycle === lc) && (!q || `${c.title} ${c.asset} ${c.asset_info?.ticker || ""} ${c.analyst?.name || ""}`.toLowerCase().includes(q.toLowerCase())));
  return (
    <div data-testid="conviction-studio">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <h2 className="font-display text-2xl font-bold flex-1">Conviction Studio</h2>
        <button data-testid="studio-new" onClick={() => setEdit({ id: null })} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#ccff00] text-black text-sm font-semibold"><Plus className="w-4 h-4" /> New conviction</button>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" /><input data-testid="studio-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, ticker, analyst…" className={`${inp} pl-9 w-64`} /></div>
        <select data-testid="studio-filter-lifecycle" value={lc} onChange={(e) => setLc(e.target.value)} className="w-40 rounded-xl bg-[#0d0d0d] border border-white/10 px-3 py-2 text-sm"><option value="">All stages</option>{["draft", "active", "closed", "cancelled"].map((x) => <option key={x} value={x}>{x[0].toUpperCase() + x.slice(1)}</option>)}</select>
      </div>
      <p className="text-xs text-zinc-500 mb-3">Click a conviction to open its editor. Editorial changes never alter the entry, publication time, signals or model performance.</p>
      <div className="space-y-3">
        {rows.map((c) => <Row key={c.id} c={c} open={(id, focus) => setEdit({ id, focus })} history={setHist} />)}
        {rows.length === 0 && <p className="text-sm text-zinc-500">No convictions match.</p>}
      </div>
      {isAdmin && <LegacyReview analysts={analysts} reload={load} />}
      {hist && <HistoryDialog id={hist} onClose={() => setHist(null)} />}
    </div>
  );
}
